import React, { useMemo } from "react";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface DatePickerProps {
    value: Date | null;
    minDate?: Date | null;
    maxDate?: Date | null;
    timezone?: string;
    onChange: (date: Date | null) => void;
    shouldDisableDate?: (date: Dayjs) => boolean;
    disabled?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    minDate,
    maxDate,
    timezone = "UTC",
    onChange,
    shouldDisableDate,
    disabled = false,
}) => {
    // Memoize the dayjs conversions to prevent creating new instances on every render
    const dayjsValue = useMemo(
        () => (value ? dayjs.tz(value, timezone) : null),
        [value, timezone],
    );

    const dayjsMinDate = useMemo(
        () => (minDate ? dayjs.tz(minDate, timezone) : undefined),
        [minDate, timezone],
    );

    const dayjsMaxDate = useMemo(
        () => (maxDate ? dayjs.tz(maxDate, timezone) : undefined),
        [maxDate, timezone],
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MuiDatePicker
                label="Select a Date"
                value={dayjsValue}
                minDate={dayjsMinDate}
                maxDate={dayjsMaxDate}
                shouldDisableDate={shouldDisableDate}
                onChange={(date) => {
                    onChange(date ? (date as Dayjs).toDate() : null);
                }}
                slotProps={{
                    textField: {
                        variant: "outlined",
                        fullWidth: true,
                        size: "small",
                        disabled,
                    },
                }}
                disablePast={false}
                disableFuture={false}
                views={["year", "month", "day"]}
                disabled={disabled}
            />
        </LocalizationProvider>
    );
};
